import React, { useState, useEffect } from 'react';
import { useContentByEmbed } from '../../hooks/useContentByEmbed';

interface PropertySearchBarProps {
  embedId: string;
  encriptedString?: string;
  onSearch?: (filters: SearchFilters) => void;
  valuationUrl?: string;
  className?: string;
}

interface SearchFilters {
  tab: 'buy' | 'sell';
  location: string;
  minPrice: string;
  maxPrice: string;
  beds: string;
  baths: string;
}

const priceOptions = [
  { value: '', label: 'Min Price' },
  { value: '100000', label: '$100,000' },
  { value: '200000', label: '$200,000' },
  { value: '300000', label: '$300,000' },
  { value: '400000', label: '$400,000' },
  { value: '500000', label: '$500,000' },
  { value: '750000', label: '$750,000' },
  { value: '1000000', label: '$1,000,000' },
  { value: '2000000', label: '$2,000,000' },
];

const maxPriceOptions = [
  { value: '', label: 'Max Price' },
  ...priceOptions.slice(1),
  { value: '5000000', label: '$5,000,000' },
  { value: '10000000', label: '$10,000,000' },
];

const bedsOptions = [
  { value: '', label: 'Beds' },
  { value: '1', label: '1+' },
  { value: '2', label: '2+' },
  { value: '3', label: '3+' },
  { value: '4', label: '4+' },
  { value: '5', label: '5+' },
];

const bathsOptions = [
  { value: '', label: 'Baths' },
  { value: '1', label: '1+' },
  { value: '2', label: '2+' },
  { value: '3', label: '3+' },
  { value: '4', label: '4+' },
];

export const PropertySearchBar: React.FC<PropertySearchBarProps> = ({
  embedId,
  encriptedString,
  onSearch,
  valuationUrl = '/valuation',
  className = '',
}) => {

  const [activeTab, setActiveTab] = useState<'buy' | 'sell'>('buy');
  const [location, setLocation] = useState('');
  const [minPrice, setMinPrice] = useState('');
  const [maxPrice, setMaxPrice] = useState('');
  const [beds, setBeds] = useState('');
  const [baths, setBaths] = useState('');

  const handleSearch = () => {
    if (!location.trim()) {
      return;
    }

    const params = new URLSearchParams();
    params.set('city', location.trim());
    if (minPrice) params.set('minPrice', minPrice);
    if (maxPrice) params.set('maxPrice', maxPrice);
    if (beds) params.set('beds', beds);
    if (baths) params.set('baths', baths);
    
    const searchUrl = `/search?${params.toString()}`;
    window.location.href = searchUrl;

    onSearch?.({
      tab: activeTab,
      location,
      minPrice,
      maxPrice,
      beds,
      baths,
    });
  };

  return (
    <div className={`property-search-bar ${className}`}>
      <style>{`
        .property-search-bar {
          font-family: 'Montserrat', sans-serif;
          background: rgba(255, 255, 255, 0.95);
          border-radius: 8px;
          padding: 20px 24px;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.15);
          max-width: 100%;
          margin: 0 auto;
        }

        .psb-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          margin-bottom: 16px;
        }

        .psb-tabs {
          display: flex;
          gap: 24px;
        }

        .psb-tab {
          background: none;
          border: none;
          font-size: 16px;
          font-weight: 500;
          color: #666;
          cursor: pointer;
          padding: 8px 0;
          border-bottom: 2px solid transparent;
          transition: all 0.2s ease;
          text-decoration: none;
        }

        .psb-tab:hover {
          color: #333;
          text-decoration: none;
        }

        .psb-tab.active {
          color: #1a1a2e;
          border-bottom-color: #c9a227;
        }

        .psb-tagline {
          font-style: italic;
          color: #888;
          font-size: 14px;
        }

        .psb-filters {
          display: grid;
          grid-template-columns: 1.5fr 1fr 1fr 0.8fr 0.8fr;
          gap: 12px;
          margin-bottom: 16px;
        }

        .psb-filter-group {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .psb-label {
          font-size: 11px;
          text-transform: uppercase;
          color: #666;
          letter-spacing: 0.5px;
        }

        .psb-input,
        .psb-select {
          padding: 10px 12px;
          border: 1px solid #ddd;
          border-radius: 4px;
          font-size: 14px;
          color: #333;
          background: white;
          transition: border-color 0.2s ease;
        }

        .psb-input:focus,
        .psb-select:focus {
          outline: none;
          border-color: #c9a227;
        }

        .psb-input::placeholder {
          color: #999;
        }

        .psb-select {
          cursor: pointer;
          appearance: none;
          background-image: url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='12' height='12' viewBox='0 0 12 12'%3E%3Cpath fill='%23666' d='M6 8L1 3h10z'/%3E%3C/svg%3E");
          background-repeat: no-repeat;
          background-position: right 12px center;
          padding-right: 32px;
        }

        .psb-search-btn {
          width: 100%;
          padding: 14px;
          background: #1a1a2e;
          color: white;
          border: none;
          border-radius: 4px;
          font-size: 14px;
          font-weight: 600;
          text-transform: uppercase;
          letter-spacing: 1px;
          cursor: pointer;
          transition: background 0.2s ease;
        }

        .psb-search-btn:hover {
          background: #b8964a;
        }

        @media (max-width: 768px) {
          .psb-filters {
            grid-template-columns: 1fr 1fr;
          }

          .psb-filter-group:first-child {
            grid-column: 1 / -1;
          }

          .psb-header {
            flex-direction: column;
            align-items: flex-start;
            gap: 8px;
          }

          .psb-tagline {
            display: none;
          }
        }

        @media (max-width: 480px) {
          .psb-filters {
            grid-template-columns: 1fr;
          }

          .psb-filter-group:first-child {
            grid-column: auto;
          }
        }
      `}</style>

      <div className="psb-header">
        <div className="psb-tabs">
          <button
            className={`psb-tab ${activeTab === 'buy' ? 'active' : ''}`}
            onClick={() => setActiveTab('buy')}
          >
            Buy a Home
          </button>
          <a
            className={`psb-tab ${activeTab === 'sell' ? 'active' : ''}`}
            href={valuationUrl}
          >
            Sell a Home
          </a>
        </div>
        <span className="psb-tagline">Exclusive listings. Exceptional living.</span>
      </div>

      <div className="psb-filters">
        <div className="psb-filter-group">
          <label className="psb-label">Location</label>
          <input
            type="text"
            className="psb-input"
            placeholder="City, County, or Postal Code"
            value={location}
            onChange={(e) => setLocation(e.target.value)}
          />
        </div>

        <div className="psb-filter-group">
          <label className="psb-label">Min Price</label>
          <select
            className="psb-select"
            value={minPrice}
            onChange={(e) => setMinPrice(e.target.value)}
          >
            {priceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="psb-filter-group">
          <label className="psb-label">Max Price</label>
          <select
            className="psb-select"
            value={maxPrice}
            onChange={(e) => setMaxPrice(e.target.value)}
          >
            {maxPriceOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="psb-filter-group">
          <label className="psb-label">Beds</label>
          <select
            className="psb-select"
            value={beds}
            onChange={(e) => setBeds(e.target.value)}
          >
            {bedsOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="psb-filter-group">
          <label className="psb-label">Baths</label>
          <select
            className="psb-select"
            value={baths}
            onChange={(e) => setBaths(e.target.value)}
          >
            {bathsOptions.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <button className="psb-search-btn" onClick={handleSearch}>
        Search
      </button>
    </div>
  );
};

export default PropertySearchBar;

